import type { AccionDeLote, ResultadoDeLote } from '../api/publicacion'

/**
 * Las casillas de los listados, sin React: qué hay marcado, qué hace «seleccionar todo lo
 * visible», qué acciones se ofrecen y cómo se cuenta lo que pasó. Qué acciones hay lo dice
 * el esquema del núcleo para quien mira (`acciones`); lo que se hace lo vuelve a decidir la
 * colección en cada id (`/api/panel/lote`).
 */

export type Acciones = Record<AccionDeLote, boolean>

export const ORDEN_DE_ACCIONES: readonly AccionDeLote[] = ['publicar', 'despublicar', 'borrar']

/** Las acciones que se ofrecen, en su orden; sin ninguna, el listado no lleva casillas. */
export const accionesDisponibles = (acciones: Partial<Acciones> | null | undefined): AccionDeLote[] => ORDEN_DE_ACCIONES.filter((a) => acciones?.[a] === true)

export const alternarUno = (seleccion: ReadonlySet<number>, id: number): Set<number> => {
  const siguiente = new Set(seleccion)
  if (siguiente.has(id)) siguiente.delete(id)
  else siguiente.add(id)
  return siguiente
}

export type EstadoDeTodos = 'algunos' | 'ninguno' | 'todos'

/** La casilla de la cabecera, respecto a **lo visible**: marcada, a medias o vacía. */
export const estadoDeTodos = (seleccion: ReadonlySet<number>, visibles: readonly number[]): EstadoDeTodos => {
  if (visibles.length === 0) return 'ninguno'
  const marcados = visibles.filter((id) => seleccion.has(id)).length
  return marcados === 0 ? 'ninguno' : marcados === visibles.length ? 'todos' : 'algunos'
}

/** «Seleccionar todo lo visible»: con todo lo visible marcado, lo desmarca; si no, lo marca. Lo que no se ve no se toca. */
export const alternarTodos = (seleccion: ReadonlySet<number>, visibles: readonly number[]): Set<number> => {
  const siguiente = new Set(seleccion)
  if (estadoDeTodos(seleccion, visibles) === 'todos') for (const id of visibles) siguiente.delete(id)
  else for (const id of visibles) siguiente.add(id)
  return siguiente
}

/**
 * Lo marcado que sigue a la vista: al cambiar de página, de filtro o al refrescar, lo que ya
 * no se ve deja de estar marcado. Una acción sobre algo que no se está viendo es una
 * sorpresa, no un atajo.
 */
export const soloVisibles = (seleccion: ReadonlySet<number>, visibles: readonly number[]): Set<number> => new Set(visibles.filter((id) => seleccion.has(id)))

const ETIQUETA: Record<AccionDeLote, string> = { publicar: 'Publicar', despublicar: 'Pasar a borrador', borrar: 'Borrar' }
export const etiquetaDeAccion = (accion: AccionDeLote): string => ETIQUETA[accion]

type Nombres = { singular: string; plural: string }

const cuantos = (n: number, nombres: Nombres) => `${n} ${(n === 1 ? nombres.singular : nombres.plural).toLowerCase()}`

/** La pregunta antes de hacerlo: cuántos y qué pasa. */
export const confirmacionDelLote = (accion: AccionDeLote, n: number, nombres: Nombres): { titulo: string; texto: string; boton: string } => {
  const que = cuantos(n, nombres)
  switch (accion) {
    case 'publicar':
      return { titulo: `¿Publicar ${que}?`, texto: 'Se publica lo último que se guardó de cada uno. En cuanto el diseño de tu web esté publicado, se verá en internet.', boton: `Publicar (${n})` }
    case 'despublicar':
      return { titulo: `¿Pasar a borrador ${que}?`, texto: 'Dejarán de verse en tu web hasta que se vuelvan a publicar. No se borra nada.', boton: `Pasar a borrador (${n})` }
    case 'borrar':
      return { titulo: `¿Borrar ${que}?`, texto: 'No se puede deshacer.', boton: `Borrar (${n})` }
  }
}

const HECHOS: Record<AccionDeLote, string> = { publicar: 'Publicados', despublicar: 'Pasados a borrador', borrar: 'Borrados' }
const YA: Record<AccionDeLote, string> = { publicar: 'ya estaban publicados', despublicar: 'ya estaban en borrador', borrar: 'ya estaban borrados' }

/** «Publicados: 18 · 1 no se pudo: «Nosotros» (Falta algo en…)». */
export const resumenDelLote = (accion: AccionDeLote, r: ResultadoDeLote): { texto: string; tono: 'aviso' | 'error' | 'ok' } => {
  const partes: string[] = []
  if (r.hechos.length > 0) partes.push(`${HECHOS[accion]}: ${r.hechos.length}`)
  if (r.yaEstaban.length > 0) partes.push(`${YA[accion]}: ${r.yaEstaban.length}`)
  if (r.fallidos.length > 0) {
    const detalle = r.fallidos
      .slice(0, 3)
      .map((f) => `«${f.titulo}» (${f.motivo.replace(/[.\s]+$/, '')})`)
      .join('; ')
    partes.push(`${r.fallidos.length === 1 ? '1 no se pudo' : `${r.fallidos.length} no se pudieron`}: ${detalle}${r.fallidos.length > 3 ? `; y ${r.fallidos.length - 3} más` : ''}`)
  }
  const texto = partes.length === 0 ? 'No se ha hecho nada.' : partes.join(' · ')
  return { texto: texto.charAt(0).toUpperCase() + texto.slice(1), tono: r.fallidos.length === 0 ? 'ok' : r.hechos.length === 0 ? 'error' : 'aviso' }
}
